// Economy engine — generates market conditions and random events

export const ECONOMY_STATES = {
  BOOM: 'boom',
  NORMAL: 'normal',
  RECESSION: 'recession',
};

export const EVENT_TYPES = {
  ECONOMIC: 'economic',
  SECTOR: 'sector',
  REGULATORY: 'regulatory',
  BLACK_SWAN: 'black_swan',
};

export const EVENTS = [
  // ─── ECONOMIC ───
  {
    id: 'boom_start',
    type: EVENT_TYPES.ECONOMIC,
    title: '📈 Economic Boom',
    description: 'GDP growth accelerates. Consumer confidence at all-time highs. Loan demand surges.',
    probability: { easy: 0.08, medium: 0.06, hard: 0.04 },
    duration: 4,
    effects: {
      economyState: ECONOMY_STATES.BOOM,
      defaultMod: 0.6,
      demandMod: 1.4,
    },
    severity: 'positive',
  },
  {
    id: 'recession_start',
    type: EVENT_TYPES.ECONOMIC,
    title: '📉 Recession Hits',
    description: 'Economic contraction. Unemployment rises. Borrowers struggle to repay.',
    probability: { easy: 0.04, medium: 0.07, hard: 0.12 },
    duration: 5,
    effects: {
      economyState: ECONOMY_STATES.RECESSION,
      defaultMod: 1.8,
      demandMod: 0.7,
    },
    severity: 'negative',
  },
  {
    id: 'rate_hike',
    type: EVENT_TYPES.ECONOMIC,
    title: '🏛️ Central Bank Rate Hike',
    description: 'Interest rates rise by 75bps. Cost of capital increases. Margins tighten.',
    probability: { easy: 0.05, medium: 0.08, hard: 0.12 },
    duration: 6,
    effects: {
      rateMod: 0.015,
      defaultMod: 1.2,
      demandMod: 0.85,
    },
    severity: 'negative',
  },
  {
    id: 'rate_cut',
    type: EVENT_TYPES.ECONOMIC,
    title: '🕊️ Rate Cut Announced',
    description: 'Central bank eases monetary policy. Borrowing becomes cheaper.',
    probability: { easy: 0.08, medium: 0.05, hard: 0.03 },
    duration: 4,
    effects: {
      rateMod: -0.01,
      defaultMod: 0.85,
      demandMod: 1.2,
    },
    severity: 'positive',
  },
  {
    id: 'inflation_spike',
    type: EVENT_TYPES.ECONOMIC,
    title: '🔥 Inflation Surge',
    description: 'CPI jumps to 8%. Real returns erode. Borrowers face higher costs.',
    probability: { easy: 0.03, medium: 0.06, hard: 0.10 },
    duration: 5,
    effects: {
      defaultMod: 1.3,
      demandMod: 1.1,
    },
    severity: 'negative',
  },
  {
    id: 'employment_boom',
    type: EVENT_TYPES.ECONOMIC,
    title: '💼 Jobs Surge',
    description: 'Unemployment hits record lows. Borrowers more creditworthy.',
    probability: { easy: 0.08, medium: 0.05, hard: 0.03 },
    duration: 3,
    effects: {
      defaultMod: 0.7,
      demandMod: 1.15,
    },
    severity: 'positive',
  },

  // ─── SECTOR ───
  {
    id: 'tech_crash',
    type: EVENT_TYPES.SECTOR,
    title: '💻 Tech Sector Crash',
    description: 'Tech bubble bursts. Startup defaults spike dramatically.',
    probability: { easy: 0.03, medium: 0.05, hard: 0.08 },
    duration: 4,
    effects: {
      sectorDefaultMod: { startup: 2.5 },
    },
    severity: 'negative',
  },
  {
    id: 'real_estate_boom',
    type: EVENT_TYPES.SECTOR,
    title: '🏠 Real Estate Boom',
    description: 'Property values surge. Individual borrowers gain collateral strength.',
    probability: { easy: 0.06, medium: 0.04, hard: 0.03 },
    duration: 3,
    effects: {
      sectorDefaultMod: { individual: 0.6 },
      demandMod: 1.2,
    },
    severity: 'positive',
  },
  {
    id: 'supply_chain_crisis',
    type: EVENT_TYPES.SECTOR,
    title: '🚢 Supply Chain Crisis',
    description: 'Global shipping disruptions. Businesses struggle with costs.',
    probability: { easy: 0.03, medium: 0.06, hard: 0.09 },
    duration: 4,
    effects: {
      sectorDefaultMod: { business: 1.8 },
    },
    severity: 'negative',
  },
  {
    id: 'startup_mania',
    type: EVENT_TYPES.SECTOR,
    title: '🦄 Startup Funding Frenzy',
    description: 'VC money flows freely. Startups flush with cash and eager to borrow more.',
    probability: { easy: 0.05, medium: 0.04, hard: 0.02 },
    duration: 3,
    effects: {
      sectorDefaultMod: { startup: 0.5 },
      demandMod: 1.3,
    },
    severity: 'positive',
  },

  // ─── REGULATORY ───
  {
    id: 'stricter_regulation',
    type: EVENT_TYPES.REGULATORY,
    title: '📋 New Banking Regulations',
    description: 'Capital requirements increase. You must maintain higher reserves.',
    probability: { easy: 0.04, medium: 0.06, hard: 0.09 },
    duration: 8,
    effects: {
      reserveRequirement: 0.05,
    },
    severity: 'neutral',
  },
  {
    id: 'tax_incentive',
    type: EVENT_TYPES.REGULATORY,
    title: '🎁 Small Business Tax Credits',
    description: 'Government incentivizes lending to small businesses. Lower defaults.',
    probability: { easy: 0.06, medium: 0.04, hard: 0.02 },
    duration: 4,
    effects: {
      sectorDefaultMod: { business: 0.7 },
    },
    severity: 'positive',
  },

  // ─── BLACK SWAN ───
  {
    id: 'financial_crisis',
    type: EVENT_TYPES.BLACK_SWAN,
    title: '🦢 Financial Crisis',
    description: 'Systemic banking crisis. Cascading defaults across all sectors. Survival mode.',
    probability: { easy: 0.005, medium: 0.01, hard: 0.025 },
    duration: 8,
    effects: {
      economyState: ECONOMY_STATES.RECESSION,
      defaultMod: 2.5,
      demandMod: 0.4,
      capitalHit: 0.15,
    },
    severity: 'catastrophic',
  },
  {
    id: 'pandemic',
    type: EVENT_TYPES.BLACK_SWAN,
    title: '🦠 Global Pandemic',
    description: 'Lockdowns devastate the economy. Mass defaults. Government intervention follows.',
    probability: { easy: 0.003, medium: 0.008, hard: 0.02 },
    duration: 10,
    effects: {
      economyState: ECONOMY_STATES.RECESSION,
      defaultMod: 2.0,
      demandMod: 0.5,
      sectorDefaultMod: { business: 2.0, startup: 1.5 },
    },
    severity: 'catastrophic',
  },
  {
    id: 'windfall',
    type: EVENT_TYPES.BLACK_SWAN,
    title: '🌟 Government Bailout Program',
    description: 'Emergency lending program. Government guarantees 50% of new loans.',
    probability: { easy: 0.01, medium: 0.005, hard: 0.002 },
    duration: 4,
    effects: {
      defaultMod: 0.4,
      demandMod: 1.5,
      capitalBoost: 0.10,
    },
    severity: 'windfall',
  },
];

export function rollEvents(difficulty, activeEventIds) {
  const triggered = [];

  for (const event of EVENTS) {
    if (activeEventIds.has(event.id)) continue;

    const prob = event.probability[difficulty] || 0.05;
    if (Math.random() < prob) {
      triggered.push({
        ...event,
        remainingMonths: event.duration,
        triggeredAt: Date.now(),
      });
    }
  }

  return triggered;
}

export function getEffectiveEconomyState(activeEvents) {
  // Last economy-changing event wins
  for (const event of [...activeEvents].reverse()) {
    if (event.effects.economyState) {
      return event.effects.economyState;
    }
  }
  return ECONOMY_STATES.NORMAL;
}

export function getAggregateEffects(activeEvents) {
  let defaultMod = 1.0;
  let demandMod = 1.0;
  let rateMod = 0;
  let reserveRequirement = 0;
  const sectorDefaultMods = { individual: 1.0, business: 1.0, startup: 1.0 };

  for (const event of activeEvents) {
    const e = event.effects;
    if (e.defaultMod) defaultMod *= e.defaultMod;
    if (e.demandMod) demandMod *= e.demandMod;
    if (e.rateMod) rateMod += e.rateMod;
    if (e.reserveRequirement) reserveRequirement = Math.max(reserveRequirement, e.reserveRequirement);
    if (e.sectorDefaultMod) {
      for (const [sector, mod] of Object.entries(e.sectorDefaultMod)) {
        sectorDefaultMods[sector] *= mod;
      }
    }
  }

  return { defaultMod, demandMod, rateMod, reserveRequirement, sectorDefaultMods };
}

export function getSeverityColor(severity) {
  switch (severity) {
    case 'positive': return '#00ff88';
    case 'negative': return '#ff8800';
    case 'neutral': return '#00aaff';
    case 'catastrophic': return '#ff0033';
    case 'windfall': return '#ffcc00';
    default: return '#ffffff';
  }
}

export function getSeverityBg(severity) {
  switch (severity) {
    case 'positive': return 'from-green-500/10';
    case 'negative': return 'from-orange-500/10';
    case 'neutral': return 'from-blue-500/10';
    case 'catastrophic': return 'from-red-600/20';
    case 'windfall': return 'from-yellow-500/10';
    default: return 'from-gray-500/10';
  }
}
